import { RiInformationLine } from "@remixicon/react";
import React, {
    ReactElement, useEffect, useMemo, useState, useCallback,
} from "react";
import { Form } from "react-bootstrap";
import { useTheme } from "../../contexts/Theme";
import {
    Time,
    calculateDateOffset,
    toInputDateHourMinute,
} from "../../date";
import Confirm from "../confirm/Confirm";
import TimezoneSelect from "../pickers/timezone/TimezoneSelect";
import Button from "../theme/button/Button";
import TimeDisplay from "../time/Time";
import Styles from "./Scheduler.module.scss";

type DateTuple = [number, number, number]
enum DateChangeSources {
    DEFAULT = "default",
    INPUT = "input",
    PRESET = "preset",
}

interface Props {
    date?: Date;
    shouldShow: boolean;
    onSelected: (date: Date, timezone: string, notes: string) => void;
    onCancel: () => void;
    confirmText?: string;
    previewText?: string;
    shortPrefix?: string;
    canAddNotes?: boolean;
    title: string;
    isPresetAutoSelect?: boolean

}

interface Preset {
    date: Date;
    dayName: string;
    showTime: boolean;
}

const LOCAL_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

function generatePresets(shortPrefix: string, minDate: Date, maxDate: Date): Preset[] {
    const todayNow = minDate.getTime();

    return [
        { date: new Date(todayNow + Time.HOUR / 2), dayName: `${shortPrefix}30 min`, showTime: false },
        { date: new Date(todayNow + Time.HOUR), dayName: `${shortPrefix}1 h`, showTime: false },
        { date: new Date(todayNow + 3 * Time.HOUR), dayName: `${shortPrefix}3 h`, showTime: false },
        { date: new Date(todayNow + 5 * Time.HOUR), dayName: `${shortPrefix}5 h`, showTime: false },
    ].filter(({ date }) => date.getTime() <= maxDate.getTime());
}

export default function Scheduler(props: Props): ReactElement | null {
    const [date, setDate] = useState(props.date ?? new Date(new Date().getTime() + Time.HOUR));
    const [localDate, setLocalDate] = useState(new Date(new Date().getTime() + Time.HOUR));
    const [stringDate, setStringDate] = useState("");
    const [timeZoneDate, setTimeZoneDate] = useState(new Date());

    const [notes, setNotes] = useState("");

    const [timeZone, setTimeZone] = useState(LOCAL_TIMEZONE);
    const minDate = Time.getDateInTimeZone(new Date(), timeZone, LOCAL_TIMEZONE);
    const theme = useTheme();

    const [presets, setPresets] = useState<Preset[]>([]);
    const shortPrefix = props.shortPrefix ? `${props.shortPrefix} ` : "";

    function adjustDateToMinimumIfEarlier(newDate: Date) {
        const currentMinDate = Time.getDateInTimeZone(new Date(), timeZone, LOCAL_TIMEZONE);
        return (currentMinDate < newDate) ? newDate : currentMinDate;
    }

    const onDateChange = useCallback((value: Date, source: DateChangeSources = DateChangeSources.INPUT) => {
        const adjustedValue = adjustDateToMinimumIfEarlier(value);
        const adjustedTz = Time.getDateInTimeZone(adjustedValue, LOCAL_TIMEZONE, timeZone);

        if (source === DateChangeSources.PRESET && props.isPresetAutoSelect) {
            props.onSelected(adjustedTz, timeZone, notes);
        } else {
            setLocalDate(adjustedValue);
            setDate(adjustedTz);

            if (source !== DateChangeSources.INPUT || stringDate === "") {
                setStringDate(toInputDateHourMinute(adjustedTz));
            }
        }
    }, [timeZone, notes]);

    const onUpdateTimer = useCallback(() => {
        setTimeZoneDate(Time.getDateInTimeZone(new Date(), timeZone, LOCAL_TIMEZONE));
    }, [timeZone]);

    useEffect(() => {
        const currentDate = new Date();
        const morningOffset = timeZoneDate.getHours() < 9 ? 0 : 1;
        const nextMorning = calculateDateOffset(morningOffset, timeZoneDate.getDay(), timeZoneDate, 9);
        const tempPresets: Preset[] = [];

        if (currentDate.getDay() === 1 || currentDate.getDay() === 2) {
            tempPresets.push({ date: calculateDateOffset(7, 4, timeZoneDate, 10), dayName: "thursday", showTime: true });
        }

        tempPresets.push(...generatePresets(shortPrefix, minDate, nextMorning));
        tempPresets.push({ date: calculateDateOffset(7, 1, timeZoneDate, 9), dayName: "monday", showTime: true });
        tempPresets.push({ date: nextMorning, dayName: morningOffset === 0 ? "today" : "tomorrow", showTime: true });

        setPresets(tempPresets);
    }, [timeZone, shortPrefix]);

    useEffect(() => {
        onUpdateTimer();
        const timer = setInterval(onUpdateTimer, 10000);
        return () => clearInterval(timer);
    }, [timeZone]);

    const sortedFilteredPresets = useMemo(() => (
        presets.dedupe(row => toInputDateHourMinute(row.date)).sortBy(row => row.date.getTime())
    ), [presets]);

    useEffect(() => {
        if (props.date) {
            onDateChange(props.date);
        } else if (stringDate === "" && presets.isNotEmpty) {
            onDateChange(sortedFilteredPresets.firstElement.date, DateChangeSources.DEFAULT);
        }
    }, [props.date, presets]);

    const labelClass = theme.isV3 ? "text-body-2-regular" : "fw-bolder";
    return (
        <Confirm
            shouldShow={props.shouldShow}
            onHide={props.onCancel}
            confirmText={props.confirmText}
            title={props.title}
            onConfirm={() => props.onSelected(date, timeZone, notes)}
        >
            <div className={Styles.scheduler}>
                <Form.Group>
                    <Form.Label className={labelClass}>Time zone</Form.Label>
                    <TimezoneSelect
                        className={theme.isV3 ? "" : "text-gray col"}
                        isRequired
                        value={timeZone}
                        onChange={selected => setTimeZone(selected)}
                    />
                    {timeZone !== LOCAL_TIMEZONE && (
                        <Form.Text className="d-block">
                            Local time in
                            {" "}
                            {timeZone}
                            :
                            {" "}
                            {timeZoneDate.toLocaleString(undefined, {
                                year: "numeric",
                                day: "numeric",
                                month: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                            })}
                        </Form.Text>
                    )}
                </Form.Group>

                <Form.Group className="mt-4">
                    <Form.Label className={labelClass}>Presets</Form.Label>

                    <div className="d-flex flex-wrap gap3-1">
                        {sortedFilteredPresets.map(presetDate => {
                            if (presetDate.date.getTime() < minDate.getTime()) {
                                return undefined;
                            }

                            const isActive = presetDate.date.getTime() === localDate.getTime();
                            const body = [
                                (presetDate.showTime ? presetDate.date.toLocaleTimeString(undefined, {
                                    hour: "numeric",
                                    minute: "numeric",
                                }) : ""),
                                presetDate.dayName,
                            ].join(" ");

                            const onClick = () => onDateChange(presetDate.date, DateChangeSources.PRESET);

                            if (theme.isV3) {
                                return (
                                    <Button
                                        key={presetDate.date.getTime()}
                                        onClick={onClick}
                                        variant="filter"
                                        size="small"
                                        isActive={isActive}
                                    >
                                        {body}
                                    </Button>
                                );
                            }

                            return (
                                <button
                                    key={presetDate.date.getTime()}
                                    className={`btn btn-outline text-gray-600 mb-1
                                        ${isActive ? "border-primary" : ""}`}
                                    type="button"
                                    onClick={onClick}
                                >
                                    {body}
                                </button>
                            );
                        })}
                    </div>
                </Form.Group>
                <Form.Group className="mt-4">
                    <Form.Label className={labelClass}>Select Custom Time</Form.Label>

                    <input
                        type="datetime-local"
                        className={theme.isV3 ? "form-control" : "form-control w-100 mb-2 col"}
                        value={stringDate}
                        onChange={evt => setStringDate(evt.target.value)}
                        min={toInputDateHourMinute(new Date())}
                        max={toInputDateHourMinute(new Date(9999, minDate.getMonth(), minDate.getDate(), 9, 0, 0))}
                        onBlur={() => onDateChange(new Date(stringDate))}
                    />
                    <Form.Text className="d-block">
                        What time do you want to schedule?
                    </Form.Text>
                </Form.Group>

                {props.canAddNotes && (
                    <Form.Group className="mt-4">
                        <Form.Label className={labelClass}>
                            Additional Comments
                            {" "}
                            <small className={theme.isV3 ? "text-dim" : "text-light"}>(optional)</small>
                        </Form.Label>

                        <Form.Control
                            as="textarea"
                            className={Styles.comments}
                            value={notes}
                            placeholder="Any notes you want to add before scheduling..."
                            onChange={evt => setNotes(evt.target.value)}
                        />
                        <Form.Text className="d-block">
                            This note will appear in the chat, only visible to agents.
                        </Form.Text>
                    </Form.Group>
                )}

                {theme.isV3 && date && (
                    <div className={theme.isV3 ? Styles.preview : "d-flex justify-content-between align-items-end"}>
                        <RiInformationLine className={Styles.icon} />
                        {` ${props.previewText} `}
                        <small className={theme.isV3 ? Styles["preview-highlight"] : "text-gray-500"}>
                            <TimeDisplay date={date} variant="date" />
                            {", "}
                            <TimeDisplay date={date} variant="time" />
                        </small>
                    </div>
                )}
            </div>
        </Confirm>
    );
}

Scheduler.defaultProps = {
    date: undefined,
    confirmText: "Schedule",
    previewText: "Mesage will be sent",
    canAddNotes: false,
    isPresetAutoSelect: true,
    shortPrefix: "",
};
